import apiClient from './client';

export interface PortalCandidateData {
  id?: number;
  userId?: number;
  name: string;
  
  // Personal Information
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  candidateTshirtSize?: string;
  maritalStatus?: 'single' | 'married' | 'divorced' | 'widowed';
  
  // Wife Information
  wifeFirstName?: string;
  wifeLastName?: string;
  wifeEmail?: string;
  wifePhone?: string;
  wifeTshirtSize?: string;
  wifeDateOfBirth?: string;
  anniversaryDate?: string;
  
  // Children Information
  childrenNamesAges?: string;
  
  // Church Planting Information
  targetLocationMississippi?: string;
  willBeSouthernBaptist?: 'yes' | 'no' | 'unsure';
  
  // Ministry Experience
  ministryLeadershipPositions?: string;
  ministryInfluences?: string;
  theologicalTraining?: string;
  sermonReferenceUrl?: string;
  
  // Images
  headshotImagePath?: string;
  familyPhotoImagePath?: string;
  wifeHeadshotImagePath?: string;
  
  // Legacy fields for backward compatibility
  spouseName?: string;
  location?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  churchName?: string;
  supportingChurch?: string;
  portalStatus?: string;
  contactInfo?: {
    phone?: string;
    email?: string;
    preferredContact?: string;
  };
  demographics?: {
    age?: number;
    ethnicity?: string;
    education?: string;
  };
  education?: {
    highSchool?: boolean;
    college?: string;
    degree?: string;
    seminary?: string;
    seminaryDegree?: string;
    additionalTraining?: string;
  };
  ministry?: {
    currentRole?: string;
    yearsInMinistry?: number;
    previousExperience?: string;
    callingStory?: string;
    visionStatement?: string;
  };
  family?: {
    maritalStatus?: string;
    spouseInfo?: {
      name?: string;
      age?: number;
      occupation?: string;
      supportLevel?: string;
    };
    children?: Array<{
      name?: string;
      age?: number;
    }>;
  };
  preferences?: {
    targetLocation?: string;
    churchSize?: string;
    demographics?: string;
    timeline?: string;
  };
  onboardingStep?: number;
  onboardingCompleted?: boolean;
  applicationSubmittedAt?: string;
  assessmentDate?: string;
  // Assessment results/outcome and internal staff notes are intentionally absent:
  // the candidate-facing API strips them. Staff read them via the hub/admin portals.
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  
  // Background Check tracking
  backgroundCheckSubmitted?: boolean;
  backgroundCheckSubmittedAt?: string;
  backgroundCheckStatus?: 'not_started' | 'started' | 'submitted' | 'received';
  backgroundCheckStatusUpdatedAt?: string;
  backgroundCheckNotes?: string;
  
  // Prepare/Enrich counseling tracking
  prepareEnrichRequested?: boolean;
  prepareEnrichRequestedAt?: string;
  prepareEnrichStatus?: 'not_started' | 'started' | 'scheduled' | 'complete';
  prepareEnrichStatusUpdatedAt?: string;
  prepareEnrichNotes?: string;
}

export interface UserRegistrationData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  username?: string;
}

export interface PortalCandidateRegistrationData extends UserRegistrationData {
  candidateData: Partial<PortalCandidateData>;
}

export const portalCandidateService = {
  // Authentication endpoints
  register: async (data: PortalCandidateRegistrationData) => {
    const response = await apiClient.post('/auth/register-portal-candidate', data);
    return response.data;
  },

  login: async (credentials: { email: string; password: string }) => {
    const response = await apiClient.post('/auth/login', credentials);
    return response.data;
  },

  logout: async () => {
    // For JWT-based auth, just clear client-side data
    // No need to call the API since tokens are stateless
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  forgotPassword: async (email: string) => {
    const response = await apiClient.post('/auth/forgot-password', { 
      email,
      portal: 'plant'
    });
    return response.data;
  },

  resetPassword: async (token: string, newPassword: string) => {
    const response = await apiClient.post('/auth/reset-password', { token, newPassword });
    return response.data;
  },

  // Portal candidate CRUD operations
  getCurrentCandidate: async () => {
    const response = await apiClient.get('/portal-candidates/me');
    return response.data;
  },

  updateCandidate: async (data: Partial<PortalCandidateData>) => {
    const response = await apiClient.put('/portal-candidates/me', data);
    return response.data;
  },

  getCandidateById: async (id: number) => {
    const response = await apiClient.get(`/portal-candidates/${id}`);
    return response.data;
  },

  // Profile management
  updateProfile: async (data: Partial<PortalCandidateData>) => {
    const response = await apiClient.put('/portal-candidates/onboarding/profile', data);
    return response.data;
  },

  getProfile: async () => {
    const response = await apiClient.get('/portal-candidates/onboarding/profile');
    return response.data;
  },

  // File uploads
  uploadHeadshot: async (file: File) => {
    const formData = new FormData();
    formData.append('headshotImage', file);
    
    const response = await apiClient.post('/portal-candidates/onboarding/upload/headshot', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000, // 60 seconds for file uploads (larger than default 10s)
    });
    return response.data;
  },

  uploadWifeHeadshot: async (file: File) => {
    const formData = new FormData();
    formData.append('wifeHeadshotImage', file);

    const response = await apiClient.post('/portal-candidates/onboarding/upload/wife-headshot', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000, // 60 seconds for file uploads (larger than default 10s)
    });
    return response.data;
  },

  uploadFamilyPhoto: async (file: File) => {
    const formData = new FormData();
    formData.append('familyPhotoImage', file);
    
    const response = await apiClient.post('/portal-candidates/onboarding/upload/family-photo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000, // 60 seconds for file uploads (larger than default 10s)
    });
    return response.data;
  },

  // Onboarding management
  getOnboardingStatus: async () => {
    const response = await apiClient.get('/portal-candidates/onboarding/status');
    return response.data;
  },

  submitApplication: async () => {
    const response = await apiClient.post('/portal-candidates/me/submit-application');
    return response.data;
  },

  // Assessment related
  getAssessmentInfo: async () => {
    const response = await apiClient.get('/portal-candidates/me/assessment');
    return response.data;
  },

  getAssessmentSchedule: async () => {
    const response = await apiClient.get('/portal-candidates/me/assessment-schedule');
    return response.data;
  },

  getAssessmentInterviews: async () => {
    const response = await apiClient.get('/portal-candidates/me/assessment-interviews');
    return response.data;
  },

  getAssessmentAttendance: async () => {
    const response = await apiClient.get('/portal-candidates/me/assessment-attendance');
    return response.data;
  },

  submitAssessmentAttendance: async (data: {
    isAttending: boolean;
    hotelNeeded: boolean;
    hotelSundayNight: boolean;
    hotelMondayNight: boolean;
    dietaryRestrictions?: string | null;
  }) => {
    const response = await apiClient.post('/portal-candidates/me/assessment-attendance', data);
    return response.data;
  },

  /**
   * Assessment reports the assessment team has published to this candidate.
   * Empty until someone on the team publishes one by hand.
   */
  getAssessmentFinalReports: async () => {
    const response = await apiClient.get('/portal-candidates/me/assessment-final-report');
    return response.data;
  },

  getAssessmentFinalReportPdf: async (assessmentId: number): Promise<Blob> => {
    const response = await apiClient.get(
      `/portal-candidates/me/assessment-final-report/${assessmentId}/pdf`,
      { responseType: 'blob' },
    );
    return response.data;
  },

  registerForAssessment: async (assessmentId: number) => {
    const response = await apiClient.post(`/assessments/${assessmentId}/register-portal-candidate`);
    return response.data;
  },

  // Progress tracking
  getProgress: async () => {
    const response = await apiClient.get('/portal-candidates/me/progress');
    return response.data;
  },

  // File uploads (if needed)
  uploadDocument: async (file: File, documentType: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    
    const response = await apiClient.post('/portal-candidates/me/documents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Utility functions
  validateStep: async (step: number, data: Partial<PortalCandidateData>) => {
    const response = await apiClient.post(`/portal-candidates/validate-step/${step}`, data);
    return response.data;
  },

  // Portal candidate specific queries
  getAvailableAssessments: async () => {
    const response = await apiClient.get('/assessments/available-for-portal-candidates');
    return response.data;
  },

  // Why Church Planting
  submitWhyChurchPlanting: async (data: any) => {
    const response = await apiClient.post('/portal-candidates/onboarding/why-church-planting', data);
    return response.data;
  },

  getWhyChurchPlanting: async () => {
    const response = await apiClient.get('/portal-candidates/onboarding/why-church-planting');
    return response.data;
  },

  // Prepare/Enrich counseling request
  requestPrepareEnrich: async (data: any) => {
    const response = await apiClient.post('/portal-candidates/onboarding/prepare-enrich', data);
    return response.data;
  },

  getPrepareEnrich: async () => {
    const response = await apiClient.get('/portal-candidates/onboarding/prepare-enrich');
    return response.data;
  },

  // Spiritual/Emotional Health survey
  submitSpiritualHealthSurvey: async (data: any) => {
    const response = await apiClient.post('/portal-candidates/onboarding/spiritual-health', data);
    return response.data;
  },

  getSpiritualHealthSurveys: async () => {
    const response = await apiClient.get('/portal-candidates/onboarding/spiritual-health');
    return response.data;
  },

  saveSpiritualHealthSurveyDraft: async (data: any) => {
    const response = await apiClient.post('/portal-candidates/onboarding/spiritual-health/draft', data);
    return response.data;
  },

  // Spouse Interview
  submitSpouseInterview: async (data: any) => {
    const response = await apiClient.post('/portal-candidates/onboarding/spouse-interview', data);
    return response.data;
  },

  getSpouseInterview: async () => {
    const response = await apiClient.get('/portal-candidates/onboarding/spouse-interview');
    return response.data;
  },

  saveSpouseInterviewDraft: async (data: any) => {
    const response = await apiClient.post('/portal-candidates/onboarding/spouse-interview/draft', data);
    return response.data;
  },

  // Ministry Transitions
  submitMinistryTransitions: async (data: any) => {
    const response = await apiClient.post('/portal-candidates/onboarding/ministry-transitions', data);
    return response.data;
  },

  getMinistryTransitions: async () => {
    const response = await apiClient.get('/portal-candidates/onboarding/ministry-transitions');
    return response.data;
  },

  // Background check submission
  submitBackgroundCheck: async (data: any) => {
    const response = await apiClient.post('/portal-candidates/onboarding/background-check', data);
    return response.data;
  },

  getBackgroundCheck: async () => {
    const response = await apiClient.get('/portal-candidates/onboarding/background-check');
    return response.data;
  },
};

// Helper functions for data transformation
export const transformUserToCandidate = (userData: UserRegistrationData): PortalCandidateRegistrationData => {
  return {
    ...userData,
    candidateData: {
      name: `${userData.firstName} ${userData.lastName}`,
      contactInfo: {
        email: userData.email,
      },
    },
  };
};

export const validateOnboardingData = (step: number, data: Partial<PortalCandidateData>): boolean => {
  switch (step) {
    case 1: // Basic Info
      return !!(data.name && data.contactInfo?.email);
    case 2: // Personal Details
      return !!(data.demographics && data.family);
    case 3: // Education & Ministry
      return !!(data.education && data.ministry);
    case 4: // Preferences & Vision
      return !!(data.preferences && data.ministry?.visionStatement);
    case 5: // Review & Submit
      return !!(data.name && data.ministry?.visionStatement);
    default:
      return false;
  }
};
